import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Divider,
  Chip,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material"; 
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import SizeFormDialog from "../sections/sizes/SizeFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";

const PAGE_SIZE = 20;

export default function SizesPage() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [filters, setFilters] = useState({ name: "", isActive: "" });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const defaultFilter = { name: "", isActive: "" };
  const handleReset = () => setFilters(cloneDeep(defaultFilter));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (size) => {
    setSelectedSize(size);
    setDialogOpen(true);
  };

  const handleMenuOpen = (event, size) => {
    setAnchorEl(event.currentTarget);
    setSelectedSize(size);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleToggleStatusClick = () => {
    handleMenuClose();
    setConfirmOpen(true);
  };

  const handleToggleStatus = async () => {
    if (!selectedSize) return;
    try {
      const newStatus = !selectedSize.isActive;
      const res = await fetchWithInterceptor(
        `/api/sizes/${selectedSize.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
          body: JSON.stringify({ isActive: newStatus }),
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed updating size status");
      }
      appContext.showMessage(
        newStatus ? "Size activated" : "Size deactivated",
        "success"
      );
      handleMenuClose();
      setSelectedSize(null);
      loadSizes(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error updating size status", "error");
    }
  };

  const handleSaveSize = async (payload) => {
    try {
      const method = selectedSize ? "PUT" : "POST";
      const url = selectedSize
        ? `/api/sizes/${selectedSize.id}`
        : "/api/sizes";
      const res = await fetchWithInterceptor(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to save size");
      }
      appContext.showMessage(
        selectedSize ? "Size updated successfully" : "Size created successfully",
        "success"
      );
      setDialogOpen(false);
      setSelectedSize(null);
      loadSizes(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error saving size", "error");
    }
  };

  const loadSizes = async (resetPage = false) => {
    if (loading) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.name) params.append("name", filters.name);
      if (filters.isActive !== "") params.append("isActive", filters.isActive);
      params.append("limit", String(PAGE_SIZE));
      params.append(
        "offset",
        String(resetPage ? 0 : paginationModel.page * PAGE_SIZE)
      );

      const response = await fetchWithInterceptor(
        `/api/sizes?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch sizes");
      const data = await response.json();
      setSizes(data.rows || []);
      setRowCount(data.total ?? 0);
      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading sizes", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSizes(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadSizes(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const columns = [
    {
      field: "name",
      headerName: "NAME",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "value",
      headerName: "VALUE",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "isActive",
      headerName: "STATUS",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? "Active" : "Inactive"}
          color={params.row.isActive ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      renderCell: (params) => (
        <>
          <Button
            size="small"
            variant="text"
            sx={{ mr: 1, minWidth: "auto", p: 0.5 }}
            onClick={() => handleEdit(params.row)}
          >
            EDIT
          </Button>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Create, edit, and deactivate sizes (e.g., "750ml"). Changes made here will be consumed by other modules in the ZYN web app (e.g., product creation).
      </Typography>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        <Paper
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            mt: 2,
            mb: 2,
          }}
        >
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" sx={{ px: 0 }}>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  label="Name"
                  name="name"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  select
                  label="Status"
                  name="isActive"
                  variant="outlined"
                  fullWidth
                  value={filters.isActive}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">ALL</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{ xs: 12, md: 2 }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button onClick={handleReset}>RESET FILTER</Button>
              </Grid>
            </Grid>
          </div>

          <Divider />

          <Grid container sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setSelectedSize(null);
                setDialogOpen(true);
              }}
            >
              CREATE SIZE
            </Button>
          </Grid>

          <DataGrid
            rows={sizes}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            disableColumnFilter
            loading={loading}
            getRowId={(row) => row.id}
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
              },
            }}
          />

          <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleMenuClose}>
            <MenuItem onClick={handleToggleStatusClick}>
              {selectedSize?.isActive ? "Deactivate" : "Activate"}
            </MenuItem>
          </Menu>
        </Paper>
      </Container>

        <SizeFormDialog
          open={dialogOpen}
          rowId={selectedSize?.id}
          size={selectedSize}
          onClose={() => {
            setDialogOpen(false);
            setSelectedSize(null);
          }}
          onSave={handleSaveSize}
        />

        <ConfirmDialog
          open={confirmOpen}
          title={
            selectedSize?.isActive
              ? "Deactivate Size"
              : "Activate Size"
          }
          message={
            selectedSize?.isActive ? (
              <>
                Are you sure you want to deactivate
                <strong style={{ fontWeight: 500 }}>
                  {" "}
                  "{selectedSize?.name}"?
                </strong>
              </>
            ) : (
              <>
                Are you sure you want to activate
                <strong style={{ fontWeight: 500 }}>
                  {" "}
                  "{selectedSize?.name}"?
                </strong>
              </>
            )
          }
          onCancel={() => setConfirmOpen(false)}
          onConfirm={async () => {
            setConfirmOpen(false);
            await handleToggleStatus();
          }}
        />
    </>
  );
}
